import React, { useState, useEffect } from 'react';
import Navbar from '../components/Navbar';
import { QRCode } from 'react-qrcode-logo';
import axios from 'axios';
import config from '../config';

function ViewBatches() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedBatch, setSelectedBatch] = useState(null);

  useEffect(() => {
    fetchBatches();
  }, []);

  const fetchBatches = async () => {
    try {
      const response = await axios.get(`${config.API_URL}/batches`);
      setBatches(response.data.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = batches.filter(b =>
    b.batchId?.toLowerCase().includes(search.toLowerCase()) ||
    b.product?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDownloadQR = (batchId) => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const link = document.createElement('a');
      link.download = `${batchId}-qr.png`;
      link.href = canvas.toDataURL();
      link.click();
    }
  };

  const getStatusColor = (status) => {
    if (status === 'recalled') return '#C0392B';
    if (status === 'pending') return '#F0B429';
    return '#22A860';
  };

  const getStatusBg = (status) => {
    if (status === 'recalled') return '#2D1515';
    if (status === 'pending') return '#2D1F00';
    return '#0D2A1A';
  };

  // ── BATCH DETAIL VIEW ──
  if (selectedBatch) {
    const qrUrl = `${config.API_URL}/verify/${selectedBatch.batchId}`;
    return (
      <div className="page">
        <Navbar />
        <div style={{ padding: '32px', maxWidth: '900px', margin: '0 auto' }}>

          {/* Back button */}
          <button
            onClick={() => setSelectedBatch(null)}
            style={{
              background: 'none',
              border: 'none',
              color: '#64748B',
              cursor: 'pointer',
              fontSize: '13px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: 0,
            }}
          >
            ← Back to All Batches
          </button>

          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '32px',
          }}>
            <div>
              <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#F0B429' }}>
                {selectedBatch.batchId}
              </h1>
              <p style={{ color: '#FFFFFF', fontSize: '16px', marginTop: '4px' }}>
                {selectedBatch.product}
              </p>
            </div>
            <span style={{
              backgroundColor: getStatusBg(selectedBatch.status),
              border: `1px solid ${getStatusColor(selectedBatch.status)}`,
              color: getStatusColor(selectedBatch.status),
              padding: '6px 16px',
              borderRadius: '4px',
              fontSize: '12px',
              fontWeight: 'bold',
              textTransform: 'uppercase',
              letterSpacing: '1px',
            }}>
              {selectedBatch.status || 'active'}
            </span>
          </div>

          <div style={{ display: 'flex', gap: '24px' }}>

            {/* Left — Full Details */}
            <div style={{ flex: 1 }}>

              {/* Recall warning */}
              {selectedBatch.status === 'recalled' && (
                <div style={{
                  backgroundColor: '#2D1515',
                  border: '1px solid #C0392B',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                }}>
                  <p style={{ color: '#E74C3C', fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
                    ⚠ This batch has been recalled
                  </p>
                  <p style={{ color: '#CBD5E1', fontSize: '12px' }}>
                    Reason: {selectedBatch.recallReason || 'N/A'}
                  </p>
                  {selectedBatch.recalledAt && (
                    <p style={{ color: '#64748B', fontSize: '12px', marginTop: '4px' }}>
                      Recalled at: {new Date(selectedBatch.recalledAt).toLocaleString('en-ZA')}
                    </p>
                  )}
                  {selectedBatch.approvedBy && (
                    <p style={{ color: '#64748B', fontSize: '12px', marginTop: '4px' }}>
                      Approved by: {selectedBatch.approvedBy}
                    </p>
                  )}
                </div>
              )}

              {/* Pending warning */}
              {selectedBatch.status === 'pending' && (
                <div style={{
                  backgroundColor: '#2D1F00',
                  border: '1px solid #F0B429',
                  borderRadius: '8px',
                  padding: '16px',
                  marginBottom: '24px',
                }}>
                  <p style={{ color: '#F0B429', fontSize: '13px', fontWeight: 'bold', marginBottom: '8px' }}>
                    ⏳ Recall Pending Manager Approval
                  </p>
                  <p style={{ color: '#CBD5E1', fontSize: '12px' }}>
                    Initiated by: {selectedBatch.recallInitiator || 'N/A'}
                  </p>
                  <p style={{ color: '#CBD5E1', fontSize: '12px', marginTop: '4px' }}>
                    Reason: {selectedBatch.recallReason || 'N/A'}
                  </p>
                </div>
              )}

              {/* Batch details card */}
              <div className="card" style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '14px', color: '#F0B429', marginBottom: '16px' }}>
                  Batch Information
                </h3>
                {[
                  ['Batch ID', selectedBatch.batchId],
                  ['Product', selectedBatch.product],
                  ['Manufacturer', selectedBatch.manufacturer],
                  ['Quantity', `${selectedBatch.quantity} units`],
                  ['Production Site', selectedBatch.productionSite || 'N/A'],
                  ['Production Date', selectedBatch.productionDate || 'N/A'],
                  ['Expiry Date', selectedBatch.expiryDate || 'N/A'],
                  ['Status', selectedBatch.status || 'active'],
                  ['Registered At', selectedBatch.createdAt ? new Date(selectedBatch.createdAt).toLocaleString('en-ZA') : 'N/A'],
                ].map(([label, value]) => (
                  <div key={label} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '8px 0',
                    borderBottom: '1px solid #1E3A6E',
                  }}>
                    <span style={{ fontSize: '12px', color: '#64748B' }}>{label}</span>
                    <span style={{
                      fontSize: '12px',
                      color: label === 'Status' ? getStatusColor(value) : '#FFFFFF',
                      fontWeight: label === 'Status' ? 'bold' : 'normal',
                      textTransform: label === 'Status' ? 'uppercase' : 'none',
                    }}>
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Scan stats */}
              <div className="card">
                <h3 style={{ fontSize: '14px', color: '#F0B429', marginBottom: '16px' }}>
                  Consumer Intelligence
                </h3>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#0B1D35',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                  }}>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#0EA5E9' }}>
                      {selectedBatch.scanCount || 0}
                    </p>
                    <p style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Total Scans
                    </p>
                  </div>
                  <div style={{
                    flex: 1,
                    backgroundColor: '#0B1D35',
                    borderRadius: '8px',
                    padding: '16px',
                    textAlign: 'center',
                  }}>
                    <p style={{ fontSize: '32px', fontWeight: 'bold', color: '#22A860' }}>
                      {selectedBatch.status === 'active' ? '✓' : '✕'}
                    </p>
                    <p style={{ fontSize: '11px', color: '#64748B', marginTop: '4px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                      Blockchain Status
                    </p>
                  </div>
                </div>
              </div>

            </div>

            {/* Right — QR Code */}
            <div className="card" style={{ textAlign: 'center', minWidth: '260px', alignSelf: 'flex-start' }}>
              <h3 style={{ fontSize: '14px', color: '#F0B429', marginBottom: '8px' }}>
                QR Code
              </h3>
              <p style={{ fontSize: '11px', color: '#64748B', marginBottom: '20px' }}>
                Download or print for product packaging
              </p>

              <div style={{
                backgroundColor: '#FFFFFF',
                padding: '16px',
                borderRadius: '8px',
                display: 'inline-block',
                marginBottom: '16px',
              }}>
                <QRCode
                  value={qrUrl}
                  size={180}
                  bgColor="#FFFFFF"
                  fgColor="#0B1D35"
                  qrStyle="dots"
                />
              </div>

              <p style={{
                fontSize: '10px',
                color: '#64748B',
                marginBottom: '20px',
                wordBreak: 'break-all',
                padding: '0 8px',
              }}>
                {selectedBatch.batchId}
              </p>

              <button
                className="btn-primary"
                onClick={() => handleDownloadQR(selectedBatch.batchId)}
                style={{ width: '100%', marginBottom: '12px' }}
              >
                ⬇ Download QR Code
              </button>

              <button
                className="btn-secondary"
                onClick={() => window.print()}
                style={{ width: '100%' }}
              >
                🖨 Print QR Code
              </button>

              {/* Verify link */}
              <div style={{
                marginTop: '16px',
                padding: '12px',
                backgroundColor: '#0B1D35',
                borderRadius: '6px',
              }}>
                <p style={{ fontSize: '11px', color: '#64748B', marginBottom: '4px' }}>
                  Verify URL
                </p>
                <p style={{
                  fontSize: '10px',
                  color: '#0EA5E9',
                  wordBreak: 'break-all',
                  lineHeight: '1.4',
                }}>
                  {qrUrl}
                </p>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // ── BATCH LIST VIEW ──
  return (
    <div className="page">
      <Navbar />

      <div style={{ padding: '32px', maxWidth: '1200px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{
          marginBottom: '32px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: 'bold', color: '#FFFFFF' }}>
              All Batches
            </h1>
            <p style={{ color: '#64748B', fontSize: '13px', marginTop: '4px' }}>
              {batches.length} batches registered · Click any row for full details and QR code
            </p>
          </div>
          <input
            className="input"
            style={{ width: '280px', marginBottom: 0 }}
            placeholder="Search by batch ID or product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Table */}
        <div className="card">
          {loading ? (
            <p style={{ color: '#64748B' }}>Loading batches...</p>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px' }}>
              <p style={{ color: '#64748B', fontSize: '13px' }}>No batches found.</p>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {['Batch ID', 'Product', 'Manufacturer', 'Qty', 'Production Date', 'Expiry Date', 'Scans', 'Status', 'Registered'].map(h => (
                    <th key={h} style={{
                      textAlign: 'left',
                      padding: '8px',
                      fontSize: '11px',
                      color: '#64748B',
                      textTransform: 'uppercase',
                      letterSpacing: '1px',
                      borderBottom: '1px solid #1E3A6E',
                    }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((batch, i) => (
                  <tr
                    key={i}
                    onClick={() => setSelectedBatch(batch)}
                    style={{
                      borderBottom: '1px solid #1E3A6E',
                      cursor: 'pointer',
                      transition: 'background 0.15s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.backgroundColor = '#1a2a43'}
                    onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#F0B429' }}>
                      {batch.batchId}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#FFFFFF' }}>
                      {batch.product}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#FFFFFF' }}>
                      {batch.manufacturer}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#FFFFFF' }}>
                      {batch.quantity}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#64748B' }}>
                      {batch.productionDate || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#64748B' }}>
                      {batch.expiryDate || 'N/A'}
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#0EA5E9' }}>
                      {batch.scanCount || 0}
                    </td>
                    <td style={{ padding: '12px 8px' }}>
                      <span style={{
                        backgroundColor: getStatusBg(batch.status),
                        border: `1px solid ${getStatusColor(batch.status)}`,
                        color: getStatusColor(batch.status),
                        padding: '3px 10px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        textTransform: 'uppercase',
                      }}>
                        {batch.status || 'active'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 8px', fontSize: '13px', color: '#64748B' }}>
                      {batch.createdAt ? new Date(batch.createdAt).toLocaleDateString('en-ZA', {
                        dateStyle: 'medium'
                      }) : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

      </div>
    </div>
  );
}

export default ViewBatches;